import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hideBackButton } from '@telegram-apps/sdk-react';
import { PhotoSlot } from '../components/design/PhotoSlot';
import { BottomNav } from '../components/design/BottomNav';
import { BottomHint } from '../components/design/BottomHint';
import { InlineCTA } from '../components/design/InlineCTA';
import { StepIndicator } from '../components/design/StepIndicator';
import { brand, fontFamily, tg } from '../theme/tokens';
import { useWizard } from '../state/wizard';
import { describePhotoError, type PhotoError } from '../lib/photoValidation';

/**
 * Home / Step 1 of the wizard — pick two faces.
 *
 * Wires per-slot photo validation:
 *   - MIME (JPEG / PNG only) and size (≤10 MB) checked synchronously on
 *     file pick. Mirrors the server's gate so users get instant feedback
 *     rather than learning at Pay time.
 *   - Face detection via native FaceDetector (Chromium only). On iOS /
 *     Safari this silently skips — server-side provider catches it.
 *   - Errors block Continue and render a full-width banner below the
 *     photos so long i18n strings (DE/UA/RU 20-30% longer than EN) fit.
 *
 * No eager upload — File objects are held in wizard state and shipped at
 * the final createGeneration call.
 */
export function HomePage() {
  const navigate = useNavigate();
  const { photo1, photo2, setPhoto1, setPhoto2 } = useWizard();

  // Errors are local UI state, not wizard state — they reset on file
  // replace and don't need to survive navigation away from /.
  const [photo1Err, setPhoto1Err] = useState<PhotoError | null>(null);
  const [photo2Err, setPhoto2Err] = useState<PhotoError | null>(null);

  const bothSelected = photo1 !== null && photo2 !== null;
  const hasErrors = photo1Err !== null || photo2Err !== null;
  const canContinue = bothSelected && !hasErrors;

  useEffect(() => {
    try { hideBackButton(); } catch { /* sdk not always ready */ }
  }, []);

  return (
    <div style={{ minHeight: '100%', display: 'flex', flexDirection: 'column', paddingBottom: 180 }}>
      <StepIndicator step={1} total={4} />

      <div style={{ padding: '18px 20px 4px', fontFamily }}>
        <h1
          style={{
            margin: '0 0 4px',
            fontSize: 28,
            fontWeight: 800,
            color: tg.text,
            letterSpacing: -0.5,
          }}
        >
          Create a kiss
        </h1>
        <div style={{ fontSize: 15, color: tg.textHint, lineHeight: 1.4 }}>
          Choose two faces — get a video
        </div>
      </div>

      <div
        style={{
          padding: '28px 20px 0',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          position: 'relative',
        }}
      >
        {/*
         * Labels are gendered ("Male" / "Female") — NOT decorative. The
         * order encodes a contract that the prompt template depends on:
         *
         *   slot 1 (left) = Male photo   → gen.input_photo_1 → imageInputs[0]
         *                                → IMAGE_REF_1 → right in final image
         *                                → linen shirt (Person B)
         *
         *   slot 2 (right) = Female photo → gen.input_photo_2 → imageInputs[1]
         *                                 → IMAGE_REF_2 → left in final image
         *                                 → floral dress (Person A)
         *
         * Image-gen providers (kie.ai Nano Banana, defapi) need the right
         * person mapped to the right role (wardrobe, position, identity
         * features) — that mapping is encoded in the prompt template by
         * IMAGE_REF index. Swapping slot order here puts the male in a
         * floral dress / female in a linen shirt, with positions also
         * mirrored. Output looks visibly wrong.
         *
         * Lock-step: if labels change, also update:
         *   - api/src/lib/pipeline.ts (imageInputs construction)
         *   - api/src/lib/retry.ts   (same)
         *   - prompts table — all image_*_2p templates that reference
         *     IMAGE_REF_1 / IMAGE_REF_2 by gender
         */}
        <PhotoSlot
          label="Male"
          file={photo1}
          onFileSelect={setPhoto1}
          onError={setPhoto1Err}
          error={photo1Err}
        />
        <HeartConnector />
        <PhotoSlot
          label="Female"
          file={photo2}
          onFileSelect={setPhoto2}
          onError={setPhoto2Err}
          error={photo2Err}
        />
      </div>

      {/* Error banner — stacks one per errored slot. Detailed copy here
          (slot itself just shows "!" + "Error" — keeps slot dimensions
          stable regardless of message length). Slot label matches the
          slot's Female/Male label so user knows which photo failed. */}
      {photo1Err && <ErrorBanner slot="Male photo" error={photo1Err} />}
      {photo2Err && <ErrorBanner slot="Female photo" error={photo2Err} />}

      <div style={{ flex: 1 }} />

      {canContinue ? (
        <InlineCTA onClick={() => navigate('/wizard/scene')}>Continue</InlineCTA>
      ) : (
        <BottomHint>{hintFor(photo1, photo2, photo1Err, photo2Err)}</BottomHint>
      )}

      <BottomNav />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
function hintFor(
  p1: File | null,
  p2: File | null,
  e1: PhotoError | null,
  e2: PhotoError | null,
): string {
  if (e1 && e2) return 'Replace both photos to continue';
  if (e1) return 'Replace the Male photo to continue';
  if (e2) return 'Replace the Female photo to continue';
  if (!p1 && !p2) return 'Add two photos to continue';
  return 'Add one more photo to continue';
}

function ErrorBanner({ slot, error }: { slot: string; error: PhotoError }) {
  const { title, hint } = describePhotoError(error);
  return (
    <div
      style={{
        margin: '20px 20px 0',
        padding: '12px 14px',
        borderRadius: 12,
        background: 'rgba(235, 85, 69, 0.12)',
        border: '0.5px solid rgba(235, 85, 69, 0.3)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        fontFamily,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          background: tg.destructive,
          color: '#fff',
          fontWeight: 700,
          fontSize: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        !
      </div>
      <div style={{ flex: 1, fontSize: 13, color: tg.text, lineHeight: 1.4 }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>
          {slot} — {title}
        </div>
        <div style={{ color: tg.textHint, fontSize: 12 }}>{hint}</div>
      </div>
    </div>
  );
}

function HeartConnector() {
  return (
    <div
      style={{
        width: 40,
        height: 40,
        borderRadius: 999,
        background: brand.solid,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: `0 4px 14px -3px ${brand.solid}88`,
        position: 'relative',
        zIndex: 2,
        margin: '0 -8px',
        marginTop: 50,
        flexShrink: 0,
      }}
    >
      <svg width={18} height={18} viewBox="0 0 24 24" fill="#fff">
        <path d="M12 21s-7-4.35-7-10a4 4 0 0 1 7-2.65A4 4 0 0 1 19 11c0 5.65-7 10-7 10z" />
      </svg>
    </div>
  );
}
